import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { collection, getCountFromServer } from 'firebase/firestore';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Textarea from '@cloudscape-design/components/textarea';
import { toast } from '@/lib/notifications';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import type { TenantId } from '@etiquetachef/types';
import { hardDeleteEmptyTenantCallable, type HardDeleteTenantCounts } from '@/lib/callables';
import { tenantDeletionErrorMessage } from './tenantDeletionError';

type Props = {
  open: boolean;
  tenantId: TenantId;
  tenantName: string;
  onClose: () => void;
};

type CountsState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; counts: HardDeleteTenantCounts }
  | { kind: 'ERROR' };

/**
 * Exclusão DEFINITIVA (hard-delete) de um tenant vazio — super-admin.
 *
 * Apaga `/tenants/{id}` + subcoleções + Storage na hora, sem a janela de
 * retenção de 90 dias do soft-delete (`DeleteTenantDialog`).
 *
 * Dois caminhos (issue #83):
 * - **Tenant vazio**: confirma digitando o NOME + motivo → `force` ausente.
 * - **Tenant com dados (purge forçado)**: quando há pagamentos/produtos/
 *   etiquetas, em vez de só bloquear, oferece o purge destrutivo com fricção
 *   extra — checkbox de ciência + digitar o **tenantId** exato + motivo →
 *   `force: true`. Usado para limpar fixtures de teste em produção.
 *
 * O guard autoritativo é server-side em `hardDeleteEmptyTenant`; aqui
 * contamos client-side só para afordância (mostrar o que será apagado).
 *
 * Origem: sem issue — commit a62adb1e3, 2026-06-11 22:55 +0200; o purge
 * forçado é da #83 (2026-06-22).
 *
 * Contrato: chama `hardDeleteEmptyTenant` com `{ tenantId, reason, force? }`;
 * sucesso navega para `/tenants` (o tenant deixou de existir). Erro vira
 * `Alert` dentro do modal (#1021); cobrança viva no gateway vira instrução de encerrar antes
 * (`tenantDeletionErrorMessage(…, 'purge')`). Irreversível.
 *
 * Usado por: `features/tenants/TenantDeleteTab.tsx` (aba "Excluir" do
 * `TenantDetailPage`).
 *
 * Dados: as contagens são `getCountFromServer` em
 * `tenants/{tenantId}/{products,labels,payments}` — as mesmas três do
 * servidor.
 *
 * Falhas conhecidas:
 *  - 2026-06-22 (#83): com a contagem client-side falhando, o diálogo caía no
 *    caminho "nome" sem `force` e o servidor recusava — operador preso. Hoje
 *    contagem `ERROR` exige o caminho forçado (ver `showForce`).
 *  - 2026-10-01 (#789): o purge não para a assinatura do gateway; o servidor
 *    recusa com `live_gateway_billing` e o operador tem de encerrar antes.
 *  - O `catch` da contagem só faz `console.error`, sem report para a
 *    telemetria (ver #926, achados).
 *  - 2026-10-25 (#1021): o erro do callable ia só por toast, atrás do overlay
 *    do Modal (mesmo achado do CRM, #174). Os `Alert type="error"` das
 *    contagens são outra coisa (aviso de dados); o erro da ação tem o próprio
 *    `Alert`, no fim do conteúdo.
 *
 * Issues: #83 (purge forçado), #789 (cobrança viva), #1021 (erro no modal).
 */
export function HardDeleteTenantDialog({
  open,
  tenantId,
  tenantName,
  onClose,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirmName, setConfirmName] = useState('');
  const [confirmId, setConfirmId] = useState('');
  const [forceAck, setForceAck] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<CountsState>({ kind: 'LOADING' });

  // Conta produtos/etiquetas/pagamentos ao abrir, para afordância.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCounts({ kind: 'LOADING' });
    // Subcoleção do tenant pelo nome — só para encurtar as três contagens.
    const sub = (name: string): ReturnType<typeof collection> =>
      collection(firestore, 'tenants', tenantId, name);
    Promise.all([
      getCountFromServer(sub('products')),
      getCountFromServer(sub('labels')),
      getCountFromServer(sub('payments')),
    ])
      .then(([p, l, pay]) => {
        if (cancelled) return;
        setCounts({
          kind: 'READY',
          counts: {
            products: p.data().count,
            labels: l.data().count,
            payments: pay.data().count,
          },
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Server revalida — afordância degrada para "deixa o servidor decidir".
        console.error('admin/hardDelete counts failed', err);
        // 2ª auditoria (2026-10-25 15:52 +0100, #1021): só ia ao console — a contagem falhar (rules,
        // rede) é o caso que a docstring cita em "Falhas conhecidas" (#926) e não deixava rastro.
        reportNonFatal('admin_hard_delete_counts_failed', serializeError(err));
        setCounts({ kind: 'ERROR' });
      });
    return (): void => {
      cancelled = true;
    };
  }, [open, tenantId]);

  function close(): void {
    setConfirmName('');
    setConfirmId('');
    setForceAck(false);
    setReason('');
    setError(null);
    setCounts({ kind: 'LOADING' });
    onClose();
  }

  const hasData =
    counts.kind === 'READY' &&
    (counts.counts.products > 0 || counts.counts.labels > 0 || counts.counts.payments > 0);
  // Só consideramos "vazio" quando a contagem confirmou READY com tudo zero.
  const confirmedEmpty = counts.kind === 'READY' && !hasData;
  // Mostra o caminho de PURGE FORÇADO quando NÃO confirmou vazio: ou tem
  // dados, ou a contagem client-side falhou (ERROR). A contagem é só
  // afordância; a fonte da verdade é o servidor. Cair no caminho "nome"
  // quando a contagem falha chamaria sem `force` → o servidor rejeitaria
  // (failed-precondition) e o operador ficaria preso. Por isso, na dúvida,
  // exigimos a confirmação forçada (que também apaga tenant vazio sem dano).
  const showForce = hasData || counts.kind === 'ERROR';
  const nameMatches = confirmName === tenantName;
  // Match exato (sem trim) — mesma regra estrita do caminho por nome; no
  // caminho destrutivo (force) não convém tolerar espaço acidental.
  const idMatches = confirmId === tenantId;
  const reasonValid = reason.trim().length > 0;
  // Caminho vazio: digita o NOME. Caminho forçado: exige ciência explícita +
  // digitar o tenantId exato — fricção maior porque é destrutivo.
  const canSubmit =
    reasonValid &&
    !submitting &&
    counts.kind !== 'LOADING' &&
    (showForce ? forceAck && idMatches : nameMatches);

  async function handleSubmit(): Promise<void> {
    if (!reasonValid) {
      setError(t('tenantDetail.actions.reasonRequired'));
      return;
    }
    if (showForce) {
      if (!forceAck || !idMatches) {
        setError(t('tenantDetail.actions.hardDeleteDialog.forceConfirmRequired'));
        return;
      }
    } else if (!nameMatches) {
      setError(t('tenantDetail.actions.hardDeleteDialog.nameMismatch'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await hardDeleteEmptyTenantCallable({
        tenantId,
        reason: reason.trim(),
        ...(showForce ? { force: true } : {}),
      });
      toast.success(t('tenantDetail.actions.hardDeleteDialog.success'));
      close();
      void navigate('/tenants');
    } catch (err) {
      // #789: cobrança viva no gateway vira instrução, não o código cru.
      setError(tenantDeletionErrorMessage(err, t, 'purge'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('tenantDetail.actions.hardDeleteDialog.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSubmit();
              }}
              disabled={!canSubmit}
              loading={submitting}
            >
              {t('tenantDetail.actions.hardDeleteDialog.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Alert type="warning">{t('tenantDetail.actions.hardDeleteDialog.warning')}</Alert>

        {counts.kind === 'LOADING' ? (
          <Box>
            <Spinner /> {t('tenantDetail.actions.hardDeleteDialog.checking')}
          </Box>
        ) : counts.kind === 'READY' && hasData ? (
          <Alert type="error" header={t('tenantDetail.actions.hardDeleteDialog.forceHeader')}>
            {t('tenantDetail.actions.hardDeleteDialog.forceWarning', {
              products: counts.counts.products,
              labels: counts.counts.labels,
              payments: counts.counts.payments,
            })}
          </Alert>
        ) : counts.kind === 'ERROR' ? (
          <Alert type="error" header={t('tenantDetail.actions.hardDeleteDialog.forceHeader')}>
            {t('tenantDetail.actions.hardDeleteDialog.forceUnknown')}
          </Alert>
        ) : confirmedEmpty ? (
          <Alert type="success">{t('tenantDetail.actions.hardDeleteDialog.empty')}</Alert>
        ) : null}

        {showForce ? (
          <Checkbox
            checked={forceAck}
            onChange={({ detail }): void => {
              setForceAck(detail.checked);
            }}
            disabled={submitting}
          >
            {t('tenantDetail.actions.hardDeleteDialog.forceAck')}
          </Checkbox>
        ) : null}

        {counts.kind === 'LOADING' ? null : showForce ? (
          <FormField
            label={t('tenantDetail.actions.hardDeleteDialog.confirmIdLabel', {
              id: tenantId,
            })}
          >
            <Input
              value={confirmId}
              onChange={({ detail }): void => {
                setConfirmId(detail.value);
              }}
              autoComplete={false}
              spellcheck={false}
              disabled={submitting}
            />
          </FormField>
        ) : (
          <FormField
            label={t('tenantDetail.actions.hardDeleteDialog.confirmNameLabel', {
              name: tenantName,
            })}
          >
            <Input
              value={confirmName}
              onChange={({ detail }): void => {
                setConfirmName(detail.value);
              }}
              autoComplete={false}
              spellcheck={false}
              disabled={submitting}
              placeholder={tenantName}
            />
          </FormField>
        )}

        <FormField label={t('tenantDetail.actions.reasonLabel')}>
          <Textarea
            value={reason}
            onChange={({ detail }): void => {
              setReason(detail.value);
            }}
            rows={3}
            disabled={submitting}
            placeholder={t('tenantDetail.actions.reasonPlaceholder')}
          />
        </FormField>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
